import { normalizeUrl } from "@/utils/normalizeUrl";

/**
 * Shared URL input helpers.
 *
 * Goals:
 * - Never "invent" a URL from arbitrary text.
 * - Allow minor paste artifacts like surrounding parentheses or trailing punctuation.
 * - Keep rules explicit at call sites (e.g. AddContentModal can further restrict platforms).
 */

export function sanitizeUrlToken(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Trim common surrounding punctuation/quotes from paste.
  const stripped = trimmed.replace(/^[(["'`]+/, "").replace(/[)\]"'`,.!?]+$/g, "");
  return stripped.trim() ? stripped.trim() : null;
}

/**
 * Strictly parse a user-provided URL.
 * - Rejects whitespace-containing strings (prevents turning sentences into URLs).
 * - Normalizes protocol using our tiny helper (adds https:// when missing).
 * - Requires http(s) and a plausible hostname (dot or localhost).
 */
export function parseHttpUrlOrNull(raw: string): string | null {
  const token = sanitizeUrlToken(raw);
  if (!token) return null;
  if (/\s/.test(token)) return null;

  const withProtocol = normalizeUrl(token);
  try {
    const url = new URL(withProtocol);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (!url.hostname) return null;
    if (!url.hostname.includes(".") && url.hostname !== "localhost") return null;
    return withProtocol;
  } catch {
    return null;
  }
}

